/**
 * This file is the public interface to watching, retrieving, and setting the
 * current guide state.
 */
import type {GuideState} from "@repo/guide-types";
import * as MobileApp from "@repo/mobile-app-integration";

import {GuideStateStorage} from "./internal/guide-state-storage.ts";

// This is the singleton instance.
// We only create it when we are first asked to do so or we reset.
let _singleton: GuideStateStorage;

/**
 * This is the state change listener we use so that we can tell the mobile app
 * when the relevant state is changed.
 */
let _listener;

/**
 * Set the current singleton.
 *
 * This is used to set the value of `_singleton` so that we can replace it
 * and update the state change listener.
 */
const setSingleton = (singleton: GuideStateStorage) => {
    const oldListener = _listener;
    _singleton = singleton;
    _listener = _singleton.on("changed", (fieldName) => {
        // NOTE: We use a listener here so that we only do this if the value
        // actually changes. We could embed this inside of the
        // GuideStateStorage class, but then it would fire from ANY instance
        // not just the one we care about; so this is the logical place to
        // implement this.
        switch (fieldName) {
            case "remainingQuota":
                MobileApp.Guide.updateRemainingQuota(
                    _singleton.current.remainingQuota,
                );
                return;

            default:
                return;
        }
    });

    // Be sure to unsubscribe from the old instance.
    oldListener?.();
};

/**
 * Accessor to ensure we only create an instance when we need it.
 *
 * The rest of the calls in this file access the singleton via this accessor.
 */
const singleton = () => {
    if (_singleton == null) {
        setSingleton(new GuideStateStorage());
    }
    return _singleton;
};

/**
 * Reset the state to the default state, with optional overrides.
 *
 * This is used is useful for tests and stories where we may need to udpate
 * the state rather than modifying one field at a time.
 *
 * No-op outside of unit tests and stories. We want state changes to occur
 * on a field-by-field basis in real usage. This would replace the entire
 * storage, causing events to stop firing and other issues. If we need such
 * a thing in future, we need to implement it on the storage itself and then
 * invoke that from here similar to the other API calls exported in this file.
 *
 * @param guideStateOverride Optional override for the guide state.
 */
export const reset = (guideStateOverride?: Partial<GuideState>): void => {
    if (process.env.STORYBOOK || process.env.NODE_ENV === "test") {
        setSingleton(new GuideStateStorage(guideStateOverride));
    }
};

/**
 * Sign up for notifications from the guide state storage.
 */
export const on: GuideStateStorage["on"] = (...args) => singleton().on(...args);

/**
 * Get the latest guide state.
 */
export const latest = (): GuideState => singleton().current;

/**
 * Set a field in the guide state.
 */
export const set: GuideStateStorage["set"] = (...args) =>
    singleton().set(...args);
